import { CaretUpDown, GearSix, SignOut, SidebarSimple } from '@phosphor-icons/react';
import type { Me, Permission } from '@sm/api';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cx } from '@/components/ui/cx';
import { Tip } from '@/components/ui/Menu';
import { NAV_ICONS, SOON_MODULES } from '@/lib/domain-ui';
import { featureState, featureVisible, hasPermission } from '@/lib/permissions';

export interface SidebarCounts {
  projects?: number;
  measurements?: number;
  production?: number;
}

interface NavDef {
  to:
    | '/'
    | '/projekti'
    | '/mjerenja'
    | '/fotodokumentacija'
    | '/proizvodnja'
    | '/analiza'
    | '/proizvodi'
    | '/kupci';
  key: keyof typeof NAV_ICONS;
  labelKey: string;
  perm: Permission;
  badge?: keyof SidebarCounts;
}

export const NAV_MAIN: NavDef[] = [
  { to: '/', key: 'dashboard', labelKey: 'nav.dashboard', perm: 'dashboard.read' },
  {
    to: '/projekti',
    key: 'projects',
    labelKey: 'nav.projects',
    perm: 'projects.read',
    badge: 'projects',
  },
  { to: '/kupci', key: 'customers', labelKey: 'nav.customers', perm: 'projects.read' },
  { to: '/proizvodi', key: 'products', labelKey: 'nav.products', perm: 'projects.read' },
  {
    to: '/mjerenja',
    key: 'measurements',
    labelKey: 'nav.measurements',
    perm: 'measurements.review',
    badge: 'measurements',
  },
  { to: '/fotodokumentacija', key: 'photos', labelKey: 'nav.photos', perm: 'photos.read' },
  {
    to: '/proizvodnja',
    key: 'production',
    labelKey: 'nav.production',
    perm: 'production.read',
    badge: 'production',
  },
  { to: '/analiza', key: 'analytics', labelKey: 'nav.analytics', perm: 'analytics.read' },
];

/** Moduli koje određuje paket (`production`, `analytics`): `off` sakriva stavku menija. */
export function navFeatureOn(me: Pick<Me, 'features'>, key: string): boolean {
  if (key !== 'production' && key !== 'analytics') return true;
  return me.features[key] !== 'off';
}

function NavRow({
  active,
  collapsed,
  label,
  icon,
  trailing,
  dimIcon,
}: {
  active: boolean;
  collapsed: boolean;
  label: string;
  icon: ReactNode;
  trailing?: ReactNode;
  dimIcon?: boolean;
}) {
  return (
    <span
      className={cx(
        'flex min-h-[38px] w-full items-center gap-[11px] rounded-md px-2.5 text-left text-sm font-medium transition-colors',
        active ? 'bg-chip text-accent' : 'bg-transparent text-text group-hover:bg-bg2',
        collapsed && 'justify-center px-0',
      )}
    >
      <span className={cx('flex', active ? 'text-accent' : dimIcon ? 'opacity-70' : 'text-muted')}>
        {icon}
      </span>
      {collapsed ? null : <span className="flex-1 truncate">{label}</span>}
      {collapsed ? null : trailing}
    </span>
  );
}

export function Sidebar({
  me,
  counts,
  collapsed,
  onToggle,
  onLogout,
  showToggle,
}: {
  me: Me;
  counts: SidebarCounts;
  collapsed: boolean;
  onToggle: () => void;
  onLogout: () => void;
  showToggle: boolean;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const soon = SOON_MODULES.filter((m) => featureVisible(featureState(me, m.key)));

  const wrap = (label: string, node: ReactNode) =>
    collapsed ? <Tip label={label}>{node}</Tip> : node;

  const isActive = (to: string) =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

  return (
    <aside
      aria-label={t('panel:nav.ariaLabel')}
      className={cx('flex h-full flex-col overflow-hidden border-r border-line bg-surface')}
    >
      <div
        className={cx(
          'flex h-16 flex-none items-center border-b border-line',
          collapsed ? 'justify-center px-2' : 'justify-between px-5',
        )}
      >
        {collapsed ? null : (
          <Link to="/" className="flex items-center">
            <span className="font-heading text-xl leading-none font-semibold tracking-[-.02em] whitespace-nowrap text-text">
              Smart Measure
            </span>
          </Link>
        )}
        {showToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? t('panel:nav.expand') : t('panel:nav.collapse')}
            aria-expanded={!collapsed}
            className="grid h-8 w-8 place-items-center rounded-md text-muted hover:bg-bg2 hover:text-text"
          >
            <SidebarSimple size={18} />
          </button>
        ) : null}
      </div>

      <div className={cx('flex-none pt-3.5 pb-1', collapsed ? 'px-2' : 'px-3')}>
        <div
          className={cx(
            'flex w-full items-center gap-2.5 rounded-lg border border-line bg-bg2 p-2.5 text-left text-text',
            collapsed && 'justify-center p-1.5',
          )}
          title={me.tenant.name}
        >
          {me.tenant.logoUrl ? (
            <span className="grid h-8 w-8 flex-none place-items-center overflow-hidden rounded-md border border-line bg-surface">
              <img src={me.tenant.logoUrl} alt="" className="h-[85%] w-[85%] object-contain" />
            </span>
          ) : (
            <span className="grid h-8 w-8 flex-none place-items-center rounded-md bg-navy font-heading text-xs font-semibold text-white">
              {me.tenant.initials ?? me.tenant.name.slice(0, 2).toUpperCase()}
            </span>
          )}
          {collapsed ? null : (
            <>
              <span className="flex min-w-0 flex-1 flex-col gap-px">
                <span className="truncate text-[13px] font-semibold">{me.tenant.name}</span>
                {me.tenant.subtitle ? (
                  <span className="truncate text-xs text-muted">{me.tenant.subtitle}</span>
                ) : null}
              </span>
              <CaretUpDown size={15} className="text-muted" aria-hidden />
            </>
          )}
        </div>
      </div>

      <nav
        aria-label={t('panel:nav.ariaLabel')}
        className={cx(
          'flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto py-2.5',
          collapsed ? 'px-2' : 'px-3',
        )}
      >
        {collapsed ? null : (
          <span className="px-2.5 pt-2.5 pb-1.5 text-[11px] font-semibold tracking-[.08em] text-muted uppercase">
            {t('panel:nav.sections.work')}
          </span>
        )}
        {NAV_MAIN.filter((n) => hasPermission(me, n.perm) && navFeatureOn(me, n.key)).map((n) => {
          const Icon = NAV_ICONS[n.key];
          const label = t(`panel:${n.labelKey}`);
          const badge = n.badge ? counts[n.badge] : undefined;
          return (
            <span key={n.to}>
              {wrap(
                label,
                <Link
                  to={n.to}
                  className="group block rounded-md"
                  aria-current={isActive(n.to) ? 'page' : undefined}
                  aria-label={collapsed ? label : undefined}
                  data-testid={`nav-${n.key}`}
                >
                  <NavRow
                    active={isActive(n.to)}
                    collapsed={collapsed}
                    label={label}
                    icon={<Icon size={18} />}
                    trailing={
                      badge ? (
                        <span
                          aria-label={t('panel:nav.badgeAriaLabel', { count: badge })}
                          className="min-w-[22px] rounded-full border border-line bg-bg2 px-[7px] py-0.5 text-center text-[11px] font-semibold text-muted"
                        >
                          {badge}
                        </span>
                      ) : undefined
                    }
                  />
                </Link>,
              )}
            </span>
          );
        })}

        {soon.length > 0 ? (
          <>
            {collapsed ? (
              <span className="my-2 h-px bg-line" />
            ) : (
              <span className="px-2.5 pt-[18px] pb-1.5 text-[11px] font-semibold tracking-[.08em] text-muted uppercase">
                {t('panel:nav.sections.soon')}
              </span>
            )}
            {soon.map((m) => {
              const label = t(`panel:${m.navKey}`);
              const Icon = m.icon;
              const to = `/uskoro/${m.slug}`;
              return (
                <span key={m.key}>
                  {wrap(
                    label,
                    <Link
                      to="/uskoro/$slug"
                      params={{ slug: m.slug }}
                      className="group block rounded-md"
                      aria-current={pathname === to ? 'page' : undefined}
                      aria-label={collapsed ? label : undefined}
                      data-testid={`nav-soon-${m.slug}`}
                    >
                      <NavRow
                        active={pathname === to}
                        collapsed={collapsed}
                        label={label}
                        dimIcon
                        icon={<Icon size={18} />}
                        trailing={
                          <span className="rounded-sm bg-chip px-[7px] py-0.5 text-[10px] font-semibold tracking-[.06em] text-accent uppercase">
                            {t('panel:nav.soonBadge')}
                          </span>
                        }
                      />
                    </Link>,
                  )}
                </span>
              );
            })}
          </>
        ) : null}
      </nav>

      <div
        className={cx(
          'flex flex-none flex-col gap-0.5 border-t border-line',
          collapsed ? 'p-2' : 'p-3',
        )}
      >
        {wrap(
          t('panel:nav.settings'),
          <Link
            to="/podesavanja"
            search={{ tab: undefined }}
            className="group block rounded-md"
            data-testid="nav-settings"
            aria-label={collapsed ? t('panel:nav.settings') : undefined}
          >
            <NavRow
              active={isActive('/podesavanja')}
              collapsed={collapsed}
              label={t('panel:nav.settings')}
              icon={<GearSix size={18} />}
            />
          </Link>,
        )}
        <div
          className={cx(
            'flex items-center gap-2.5 pt-2.5 pb-1',
            collapsed ? 'flex-col px-0' : 'px-2.5',
          )}
        >
          <span
            className="grid h-[34px] w-[34px] flex-none place-items-center rounded-full bg-chip text-xs font-semibold text-accent"
            title={me.user.name}
          >
            {me.user.initials}
          </span>
          {collapsed ? null : (
            <span className="flex min-w-0 flex-1 flex-col gap-px">
              <span className="truncate text-[13px] font-semibold">{me.user.name}</span>
              <span className="text-xs text-muted">{t(`common:role.${me.role}`)}</span>
            </span>
          )}
          <button
            type="button"
            onClick={onLogout}
            aria-label={t('panel:shell.user.logout')}
            title={t('panel:shell.user.logout')}
            className="grid h-8 w-8 place-items-center rounded-md text-muted hover:bg-bg2 hover:text-text"
            data-testid="logout"
          >
            <SignOut size={17} />
          </button>
        </div>
      </div>
    </aside>
  );
}
